/**
 * Core module.
 * @module video/Core
 */

import Event from 'f.core.event';
import {
    VideoEvents,
    videoPropertiesByGet,
    videoPropertiesBySet,
} from './video';

let videos = {};

let currentVideo = '';
let index = 0;
let uuid = () => `video_${index++}`;

/**
 * 字符串首字母转为大写
 *
 * @param {string} string
 * @return {string} 首字母大写的字符串
 */
const toTitleCase = string => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * 给dom设置属性
 *
 * @param {any} el
 * @param {any} name
 * @param {any} val
 * @return {undefined}
 */
const setAttribute = (el, name, val) => el.setAttribute(name, val);


/**
 * 加载video
 *
 * @param {dom} el
 * @return {undefined}
 */
const loadVideo = el => {
    if (typeof el.load === 'function') {
        // wrapping in an iife so it's not deoptimized (#1060#discussion_r10324473)
        ((() => {
            try {
                el.load();
            } catch (e) {
                // satisfy linter
            }
        })());
    }
};

/**
 * 重置video
 *
 * @param {dom} el
 * @return {undefined}
 */
const resetVideo = el => {
    if (!el) {
        return;
    }

    let sources = el.querySelectorAll('source');
    let i = sources.length;

    while (i--) {
        el.removeChild(sources[i]);
    }

    // remove any src reference.
    // not setting `src=''` because that throws an error
    el.removeAttribute('src');
    loadVideo(el);
};

/**
 * 创建video
 *
 * @param {object} options
 * @property {boolean} options.inline 是否内联播放
 * @property {boolean} options.muted 是否预加载
 * @property {boolean} options.controls 是否显示控制条
 * @property {boolean} options.autoplay 是否自动播放
 * @property {string} options.poster 封面图地址
 * @property {string} options.width 视频宽度
 * @return {HTMLElement} video元素
 */
const createVideo = options => {
    let el = document.createElement('video');

    setAttribute(el, 'id', 'player');

    // if (options.preload) {
    //     setAttribute(el, 'preload', 'auto');
    // } else {
    //     setAttribute(el, 'preload', 'meta');
    // }

    // setAttribute(el, 'preload', 'none');

    // el.className = 'c-video__meta';
    if (options.inline) {
        setAttribute(el, 'playsinline', 'playsinline');
        setAttribute(el, 'webkit-playsinline', 'webkit-playsinline');
    }
    if (options.muted) {
        setAttribute(el, 'muted', 'muted');
    }
    if (options.controls) {
        setAttribute(el, 'controls', 'true');
    }
    if (options.autoplay) {
        setAttribute(el, 'autoplay', 'autoplay');
    }
    if (options.poster) {
        setAttribute(el, 'poster', options.poster);
    }
    // setAttribute(el, 'src', options.src);
    loadVideo(el);
    el.width = options.width;
    return el;
};


/**
 * 缓存video
 *
 * @param {object} core
 */
const regVideo = core => {
    if (typeof videos[core.__uuid] == 'undefined') {
        videos[core.__uuid] = core;
    }
};


/**
 * 取消video缓存
 *
 * @param {object} core
 */
const unRegVideo = core => {
    if (typeof videos[core.__uuid] !== 'undefined') {
        delete videos[core.__uuid];
    }
};

/**
 * video核心core
 */
class Core {
    /**
     * Creates an instance of Core.
     * @param {any} options
     */
    constructor(options) {
        this.init(options);
    }

    /**
     * 初始化
     *
     * @param {object} options
     * @property {boolean} options.inline 是否内联播放
     * @property {boolean} options.muted 是否预加载
     * @property {boolean} options.controls 是否显示控制条
     * @property {boolean} options.autoplay 是否自动播放
     * @property {string} options.poster 封面图地址
     * @property {string} options.width 视频宽度
     */
    init(options) {
        this.event = new Event();
        this.__isPlayed = false; // 是否在播放
        this.__uuid = uuid();
        regVideo(this);
        this.el = createVideo(options); // 创建video标签
        this.initEvent();
    }

    /**
     * custom Event
     * begin 当一个视频源第一次播放的时候，会触发一次此事件。
     * swtich 当使用src()播放另外一个视频的时候，会触发一次此事件，有两个参数oldsrc, newsrc
     */
    initEvent() {
        const _this = this;

        $(this.el).on('play', () => {
            _this.__isPlayed = true;
        });

        $(this.el).on('pause', () => {
            _this.__isPlayed = false;
        });

        // 对视频的第一次播放进行处理
        // var begin = function(event) {
        //     if (this.__isPlayed == false) {
        //         this.__isPlayed = true;
        //         this.event.trigger('begin');
        //     }
        // }.bind(this);

        // this.on('timeupdate', begin);

        // this.on('playing', function() {
        //     // console.log('playing');
        //     currentVideo = this;
        // }.bind(this));

        // var __this = this;
        // Events.forEach(function (eventName) {
        //   (function (evnetName) {
        //     __this.on(eventName, function () {
        //       divLog(eventName, 'is fire');
        //       console.log(eventName, 'is fire');
        //     });
        //   }(eventName));
        // });
    }


    /**
     * 注册video事件或自定义事件
     *
     * @param {string} eventName
     * @param {function} callback
     */
    on(eventName, callback) {
        if (VideoEvents.includes(eventName)) {
            this.el.addEventListener(eventName, callback, false);
        } else {
            this.event.on(eventName, callback);
        }
    }

    /**
     * 解绑video事件或自定义事件
     *
     * @param {string} eventName
     * @param {function} callback
     */
    off(eventName, callback) {
        if (VideoEvents.includes(eventName)) {
            this.el.removeEventListener(eventName, callback);
        } else {
            this.event.off(eventName, callback);
        }
    }

    /**
     * 当前video暂停
     */
    pause() {
        this.el.pause();
    }

    /**
     * 当前video播放
     */
    play() {
        const playPromise = this.el.play();
        // Catch/silence error when a pause interrupts a play request
        // on browsers which return a promise
        if (playPromise !== undefined && typeof playPromise.then === 'function') {
            playPromise.then(null, e => {});
        }
    }

    /**
     * 设置当前video-currentTime
     *
     * @param {number} seconds
     */
    setCurrentTime(seconds) {
        try {
            this.el.currentTime = seconds;
        } catch (e) {
            console.log(e, 'Video is not ready. (Video.js)');
            // this.warning(VideoJS.warnings.videoNotReady);
        }
    }

    /**
     * 加载video
     */
    load() {
        loadVideo(this.el);
    }

    /**
     * 隐藏video
     */
    hide() {
        this.el.style.display = 'none';
    }

    /**
     * 显示video
     */
    show() {
        this.el.style.display = 'block';
    }

    /**
     * 重置video
     */
    reset() {
        resetVideo(this.el);
        this.__isPlayed = false;
    }

    /**
     * 设置video播放源路径
     *
     * @param {string} src
     */
    src(src) {
        if (src === undefined) {
            return this.el.src;
        }

        this.reset();
        this.event.trigger('switch', this.el.src, src);
        setAttribute(this.el, 'src', src);
    }

    /**
     *
     * @param {any} argument
     */
    initLoading(argument) {
        let img = '';
        img += '<div class="loading" style="width:100%;height:100%;position:absolute;top:0;left:0;">';
        img += '<div class="loadingbanner"></div>';
        img += '</div>';
        document.getElementById('js_video_container').innerHTML = img;
    }

    /**
     * 渲染video-dom
     *
     * @param {HTMLELEMENT} container
     */
    renderTo(container) {
        container.appendChild(this.el);
    }

    /**
     * 销毁当前video并解绑事件
     */
    destory() {
        // 解除所有事件绑定
        VideoEvents.forEach(eventName => {
            this.off(eventName);
        });
        this.off('begin');
        this.off('switch');

        // 重置
        this.reset();

        // 移除dom节点。
        if (this.el.parentNode) {
            this.el.parentNode.removeChild(this.el);
        }

        // 取消在全局的注册
        unRegVideo(this);
    }

    /**
     * 获取video-buffer长度
     *
     * @returns {number} bufferer.length
     */
    getBuffered() {
        const buffered = this.el.buffered;
        return buffered.length === 0 ? 0 : buffered.end(buffered.length - 1);
    }

    /**
     * 获取当前video
     *
     * @static
     * @returns {object} currentVideo
     */
    static current() {
        return currentVideo;
    }

    /**
     * 获取所有注册的video
     *
     * @static
     * @returns {object} videos
     */
    static videos() {
        return videos;
    }
}


videoPropertiesByGet.forEach(prop => {
    Core.prototype[`get${toTitleCase(prop)}`] = function() {
        return this.el[prop];
    };
});

// Wrap native properties with a setter in this format:
// set + toTitleCase(name)
videoPropertiesBySet.forEach(prop => {
    Core.prototype[`set${toTitleCase(prop)}`] = function(v) {
        this.el[prop] = v;
    };
});

// wrap native functions with a function
[
    /**
     * Pause for core
     *
     * @method Core.prototype.pause
     */
    'pause',
].forEach(prop => {
    Core.prototype[prop] = function() {
        return this.el[prop]();
    };
});

/**  */
export default Core;



// WEBPACK FOOTER //
// ./components/f.component.video.pc/app/modules/core/index.js